/**
 * Контракт «git worktree» — per-session рабочий каталог (ADR-114, этап 1
 * roadmap 0.13 — блок 1.4.1a; спека подсистемы — `docs/WORKTREES.md` §1–§2).
 *
 * Worktree — вторичная рабочая копия репозитория: у каждой сессии может быть
 * свой каталог, чтобы правки соседних сессий не конфликтовали по рабочим
 * файлам и индексу (ADR-114).
 *
 * Здесь только контракты (тип записи + Zod-схемы HTTP-входа) — без
 * бизнес-логики: создание каталога (`git worktree add`), привязка сессии и
 * резолв рабочего каталога живут в core (следующий шаг).
 */
import { z } from 'zod';
/** Канонический набор стратегий worktree. */
export const WORKTREE_STRATEGIES = ['git'];
/** Канонический набор действий `worktree.updated`. */
export const WORKTREE_UPDATE_ACTIONS = [
    'created',
    'removed',
    'pruned',
    'attached',
    'detached',
];
// ── Zod-схемы (HTTP-вход и запись) ─────────────────────────────────────────────
/** Формат slug'а имени каталога worktree: строчные буквы/цифры, `-_.` внутри. */
export const WORKTREE_NAME_REGEX = /^[a-z0-9][a-z0-9._-]{0,63}$/;
/** Предельная длина имени каталога worktree. */
export const WORKTREE_NAME_MAX_LENGTH = 64;
/** Символы, запрещённые в имени ветки (набор — как в `git check-ref-format`). */
const FORBIDDEN_BRANCH_CHARS = ['~', '^', ':', '?', '*', '[', '\\'];
/**
 * Допустимо ли имя ветки: непустое, без пробелов, без `..` и без
 * запрещённых символов (`~`, `^`, `:`, `?`, `*`, `[`, `\`).
 */
export function isValidWorktreeBranch(branch) {
    if (branch.length === 0)
        return false;
    if (/\s/.test(branch))
        return false;
    if (branch.includes('..'))
        return false;
    return !FORBIDDEN_BRANCH_CHARS.some((char) => branch.includes(char));
}
/**
 * Допустим ли путь репозитория: относительный (без корня и без диска
 * Windows) и без сегмента `..` — как `repo_path` в ADR-076.
 */
export function isValidWorktreeRepoPath(repoPath) {
    if (repoPath.trim().length === 0)
        return false;
    if (repoPath.startsWith('/') || repoPath.startsWith('\\'))
        return false;
    if (/^[a-zA-Z]:[\\/]/.test(repoPath))
        return false;
    return !repoPath.split(/[\\/]+/).includes('..');
}
/** Полная запись worktree (ответы API). */
export const worktreeRecordSchema = z.object({
    id: z.string().min(1),
    projectId: z.string().min(1),
    repoPath: z.string().min(1).optional(),
    directory: z.string().min(1),
    branch: z.string().min(1),
    name: z.string().regex(WORKTREE_NAME_REGEX),
    strategy: z.enum(['git']),
    createdAt: z.string().min(1),
    updatedAt: z.string().min(1),
    createdBy: z.string().min(1).optional(),
});
/**
 * Тело создания worktree (`POST /api/v2/projects/:id/worktrees`).
 *
 * Валидация: `name` — slug (`WORKTREE_NAME_REGEX`), `branch` — без пробелов,
 * `..` и запрещённых символов, `repoPath` — относительный путь без `..`.
 */
export const worktreeCreateSchema = z.object({
    branch: z
        .string()
        .min(1)
        .refine(isValidWorktreeBranch, {
        message: 'Имя ветки недопустимо: без пробелов, «..», «~», «^», «:», «?», «*», «[», «\\»',
    })
        .optional(),
    name: z.string().regex(WORKTREE_NAME_REGEX).optional(),
    repoPath: z
        .string()
        .min(1)
        .refine(isValidWorktreeRepoPath, {
        message: 'Путь репозитория должен быть относительным и без «..»',
    })
        .optional(),
    from: z.string().min(1).optional(),
});
/**
 * Тело операции prune (`POST /api/v2/projects/:id/worktrees/prune`).
 *
 * Параметров у операции нет: проект берётся из пути, чистятся записи без
 * каталога (ADR-114 §7.3). Схема — строгая: лишние поля — ошибка валидации,
 * а не молчаливое игнорирование.
 */
export const worktreePruneSchema = z.object({}).strict();
